/**
 * The words on the work record, and the tab strip that shapes it.
 *
 * `WORK_TABS` is the load-bearing table: four tabs are filters over the one
 * history, keyed by entry kind, and the copy tables beneath it are typed off
 * the tabs themselves so a new tab fails to compile until somebody writes its
 * sentences. What each tab shows is decided in `work.model.ts`; the chain's
 * own vocabulary is `provenance.constants.ts`'s.
 *
 * Every panel the registry cannot fill yet says what it is waiting on. A
 * planned tab is reachable rather than hidden, for the reason the rail's
 * planned destinations are.
 */

import type {
  DisclosureAudience,
  ProvenanceEntryKind,
  ProvenanceField,
} from "@ondo/shared";
export const WORK_RECORD_COPY = {
  loading: "Loading this work…",
  notFound:
    "No work here. Either it is not in a collection you can see, or the link is stale — the registry gives the same answer to both, deliberately.",
  backToCollection: "← Back to my collection",
  provenanceTitle: "Artwork history",
  provenanceHelp:
    "Everything the record holds about this work — who owned it, where it was shown, what has been written about it, how it has been kept. Each entry is a claim somebody made; the count beside it is who has put their own name behind it.",
  addEntry: "+ Add history",

  /**
   * The title on a disabled "+ Add history", for a seat below editor.
   *
   * Names the rung rather than saying "not permitted", because the reader is
   * not being refused as a person — the firm holds the mandate and may write
   * this history; their seat is the part that does not.
   */
  historyNeedsEditor:
    "Writing a work’s history commits the firm that represents it, so it needs a seat at editor level or above.",
  /**
   * Appended to every filtering tab's own sentence by `toPanel`.
   *
   * Said on all four rather than left to be inferred: these tabs look like four
   * more records and are four readings of one, and a reader who does not know
   * that goes looking for the exhibition they just added on the wrong tab. One
   * place says it, so no tab can be the one that forgot to.
   */
  filteredNote: "The same record, narrowed — everything here is also on Artwork history.",
  /**
   * An empty record, said on the tab that holds all of it.
   *
   * `PROVENANCE_COPY.entriesEmpty` — "No provenance entries yet." — was what
   * fell through here, and it is wrong twice over: it says *provenance* on a tab
   * called Artwork history, and unlike all four filtering tabs it does not name
   * the way in. The first collector to open a record they have just registered
   * is the reader who needs that sentence most.
   */
  provenanceEmpty:
    "Nothing recorded yet. “+ Add history” records the first — a sale, an exhibition, a publication, a document, a condition report, or where the work has been.",
  /**
   * The one-way door, said before it is walked through.
   *
   * An entry is frozen the moment it is recorded, because an entry somebody has
   * attested to must still say what they attested to. There is no edit
   * afterwards and no undo — only a withdrawal, which leaves the row and the
   * attestation on it exactly where they were.
   */
  saveIsFinal:
    "Once saved, this entry becomes part of this work’s record. It cannot be edited afterwards, only withdrawn. Check it now.",
  saveEntry: "Save entry to the record",
  saving: "Saving…",
  unknownError: "Something went wrong. Please try again.",
} as const;

/**
 * WHO MADE THIS, AND WHETHER ANYBODY WITH AUTHORITY AGREES — OND-20.
 *
 * Five sentences for four states, and the fifth is the whole point of this
 * block. `not_requested` covers two different things: nobody was ever asked,
 * and an authority WAS asked and ended the request without taking a position —
 * declined, did not recall, passed it on, or the ask was withdrawn. The second
 * leaves `state` at `not_requested` because no attestation was written, and a
 * screen reading the state alone would tell a collector "nobody has been asked"
 * about a studio that read the request and walked away. `lastOutcome` is on
 * `WorkAttribution` for exactly this, and `closed` below is what it is for.
 *
 * SILENCE IS NEITHER ACCEPTANCE NOR REJECTION, and every sentence here is
 * written to keep that true in both directions: a declining studio has not
 * rejected the work, and an unanswered request is not a quiet confirmation.
 * Nothing in this flow expires — OND-87 forbids inventing a timer — so a
 * request open for three years reads `awaiting` and says so.
 *
 * NO NAME IS PRINTED UNLESS AN ANSWER CARRIES ONE. `answeredByName` is null in
 * `not_requested` and `awaiting`, which is the point of the field rather than an
 * omission: silence has nobody to attribute it to. `WorkAttribution` carries no
 * counterparty either — who was asked stays between the two of them, the same
 * restraint `provenance_requests_read` keeps — so the `awaiting` sentence is
 * written from the DATE and never from a name it does not hold.
 *
 * AND AN ANSWER FROM SOMEBODY ONDO NEVER APPOINTED READS WEAKER, IN WORDS AND
 * NOT ONLY IN A TONE — OND-215. The two `_unappointed` sentences say who
 * answered, say the domain the answer came from, and then say plainly what the
 * answer is NOT: ONDO has appointed nobody to speak for the artist here, so
 * this is the word of whoever controls an address and nothing further. They
 * claim no more than that, which is the same restraint `claims.test.ts` holds
 * the witness copy to — a record may say a thing was said, never that it is
 * true.
 */
export const WORK_ATTRIBUTION_COPY = {
  title: "Who made this",

  /**
   * The tag, which is a state and never a verdict about the work.
   *
   * `unanswered` is not a fifth `AttributionState`; it is `not_requested` with
   * an outcome behind it, and it exists because "NOT CONFIRMED" over a studio
   * that declined is the same words the registry uses for a work nobody has
   * mentioned to anybody. They are different facts and a reader deciding
   * whether to buy something needs them apart.
   */
  tags: {
    not_requested: "NOT CONFIRMED",
    unanswered: "ASKED — NO ANSWER EITHER WAY",
    awaiting: "AWAITING AN ANSWER",
    confirmed: "CONFIRMED",
    disputed: "DISPUTED",
    /**
     * OND-215. The word that does the work is "APPOINTED", not a hedge: a
     * reader who takes in only the tag must not come away thinking an
     * authority confirmed this, and must not come away thinking nothing
     * happened either. Both halves of the fact, in a label.
     */
    confirmed_unappointed: "CONFIRMED, NOT BY AN APPOINTED AUTHORITY",
    disputed_unappointed: "DISPUTED, NOT BY AN APPOINTED AUTHORITY",
  } as const,

  /**
   * Under the name, while the claim stands unanswered. Naming an artist is not
   * confirmation, which is the sentence OND-20 exists to make true.
   */
  claimNote:
    "This is the attribution on the record — what the registering party says, frozen on its own entry. It is not a confirmation.",

  /**
   * The same note once somebody with authority HAS answered — OND-99, and it is
   * the smallest version of this ticket's whole subject.
   *
   * `claimNote` was printed under the name in every state, so a reader of a
   * confirmed work met "It is not a confirmation." immediately above "Confirmed
   * by Erwin Wurm Studio on 04 October 2026." Both sentences were true and they
   * were about different things — the claim row and the attestation on it are
   * two rows, and the panel shows both — but a reader cannot be expected to
   * supply that distinction while deciding whether to believe a £2.2m record.
   * Two labels contradicting each other side by side is what OND-99 is for, and
   * this one was inside its scope rather than OND-63's, which flagged it.
   *
   * It still says the claim is only a claim, because that does not stop being
   * true when an answer arrives: a studio's confirmation is a separate act with
   * its own author and date, and a dispute is too. What it no longer does is
   * deny the answer printed under it.
   */
  claimNoteAnswered:
    "This is the attribution on the record — what the registering party says, frozen on its own entry. The authority’s answer to it is below.",

  /**
   * No claim on the record at all: nobody has written down who made this, so
   * there is nothing for a studio to have answered.
   */
  noClaim: "No studio has been asked to confirm who made this.",
  noClaimDetail:
    "The artist named on this record is an attribution, not a confirmation. Recording the claim is what makes it something an authorised studio, estate or foundation can be asked to accept or reject.",
  /**
   * And the case where asking is not possible. Said here as well as on the chip
   * because this panel is where somebody looks for the answer, and "no" with no
   * reason reads as a fault.
   */
  noClaimNoArtist:
    "Nobody can be asked about this work as it stands: its artist is a name typed into the record rather than one of the artists ONDO holds, and authority to answer is held over an artist ONDO holds.",

  /** The claim is on the record and nobody has been asked about it yet. */
  notRequested:
    "No authority has been asked to confirm this. The claim stands as the record’s own, and a catalogue number is issued by a confirmation and by nothing else.",

  /**
   * A live request, worded from the date because there is no name to print.
   *
   * `requestedAt` is null only if the state were reached without a request,
   * which `ondo.attribution_of()` does not do — the undated sentence is the
   * honest fallback rather than a guessed day.
   */
  awaiting: (day: string) =>
    `Asked on ${day}. No answer yet — and no answer is not a refusal: nothing in this expires, and silence is neither acceptance nor rejection.`,
  awaitingUndated:
    "An authority has been asked and has not answered. No answer is not a refusal: nothing in this expires, and silence is neither acceptance nor rejection.",

  confirmed: (name: string, day: string) =>
    `Confirmed by ${name} on ${day}. An authorised authority for this artist has put its name to the claim that this work is by them.`,
  disputed: (name: string, day: string) =>
    `Disputed by ${name} on ${day}. An authorised authority for this artist has put its name AGAINST the claim. The registry records the disagreement and does not resolve it.`,

  /**
   * AN ANSWER FROM SOMEBODY ONDO HAS NOT APPOINTED — OND-215.
   *
   * The domain is said where there is one, because it is the half a reader can
   * actually weigh: an answer from the studio's own domain and an answer from a
   * free mailbox are different pieces of evidence. The full address is said
   * nowhere, and not merely omitted here — `WorkAttribution` cannot carry one.
   *
   * Each sentence ends on what the answer is not, because a reader arriving at
   * "Confirmed by …" has already read it as a confirmation by then.
   */
  confirmedUnappointed: (name: string, day: string, domain: string | null) =>
    `Confirmed by ${name} on ${day}${domain === null ? "" : `, at an address on ${domain}`}. ONDO has not appointed them to answer for this artist, so this records that they accepted the claim and nothing beyond that. It is weaker than a confirmation from an authorised studio, estate or foundation, and it issues no catalogue number.`,
  disputedUnappointed: (name: string, day: string, domain: string | null) =>
    `Disputed by ${name} on ${day}${domain === null ? "" : `, at an address on ${domain}`}. They have put their name AGAINST the claim. ONDO has not appointed them to answer for this artist, so the registry records what they said and neither resolves it nor treats it as an authority’s objection.`,

  /**
   * An answer with no name on it. Printed rather than left as a gap.
   *
   * Says "somebody" rather than "an authority" since OND-215: the four answered
   * states include two in which nobody appointed answered, and this sentence
   * cannot see which it is being printed for.
   */
  answeredUnnamed: "Somebody has answered. The name they answered under is not shown here.",

  /**
   * THE REQUEST ENDED WITH NOBODY TAKING A POSITION, per outcome.
   *
   * Every one of these says what happened and then says what it is NOT, because
   * each is read by somebody looking for a yes or a no and each is neither. A
   * `Record` over the whole outcome union rather than the four that leave the
   * state at `not_requested`: `attested` is reachable here too — an authority
   * answered and later WITHDREW its attestation, which leaves the row's outcome
   * set and no live position on the entry — and `expired` has had no writer
   * since the outcome was invented but is in the type.
   */
  closed: {
    attested:
      "An authority answered and has since withdrawn its position, so nothing now stands for or against the claim. The answer it gave is still on the claim’s own entry.",
    declined:
      "An authority was asked and declined to answer. Nothing was recorded for or against the claim — a decline is not a rejection of the work.",
    no_recall:
      "An authority was asked and answered that it does not recall the work. That closes the request with no position taken, which is not a no.",
    referred:
      "An authority was asked and passed the question on to somebody better placed. The question is open with them; nobody has answered it yet.",
    withdrawn:
      "The request was withdrawn before it was answered, so no authority took a position on this.",
    expired: "The request closed without an answer either way.",
  } as const,

  /** Opens the modal on the Artist attribution chip. */
  ask: "Ask an authority to confirm this →",
  /** The claim's own row on the history, where the request door is. */
  openClaim: "Open the claim on the history",
  openClaimToAsk: "Open the claim to ask an authority",
  openClaimToAskAgain: "Open the claim to ask again",
} as const;

export const WORK_FIELD_LABELS = {
  title: "Title",
  artist: "Artist",
  year: "Year",
  medium: "Medium",
  dimensions: "Dimensions",
  edition: "Edition",
  registry: "Registry",
  collection: "Collection",
  holder: "Held by",
  location: "Location",
  /**
   * OND-58. Shown to the attributed estate on a work it does not own, and the
   * word is "Owner" rather than "Collector" because the row names whoever holds
   * title — which may be an institution or a gallery, not a collector.
   */
  owner: "Owner",
} as const;

/**
 * The owner's two estate controls — OND-58.
 *
 * FOUR SENTENCES FOR FOUR STATES, and none of them is shared between the two
 * controls. The product rule is that naming the owner is not a grant of record
 * access, so a line a reader could take as describing both switches would
 * undo in copy what the schema is careful to keep apart.
 *
 * The estate is never NAMED here. `ondo.works.attributed_principal_id` is in no
 * write grant and the estate's own name is unreachable through
 * `principals_read`, so these sentences say "the artist's estate" and the one
 * fact the owner is given is whether there is one.
 *
 * WHAT THE SECOND SWITCH DOES NARROWED ON 14 OCTOBER — OND-57,
 * `20261014091000`. The history and its documents left it and follow attribution
 * now: the estate reads them on every work by its artist, whoever owns it, and
 * no owner can switch that off. So three of these sentences were FALSE and are
 * rewritten, and the switch is relabelled for what it still decides — where the
 * object is kept, which is a physical-security fact with a rule of its own.
 *
 * The lead states the part the owner cannot change FIRST, for the reason the
 * note below gives about a permissive default: a control panel whose heading is
 * "what the estate can see" must not let an owner infer that the switches are
 * the whole answer.
 */
export const ESTATE_DISCLOSURE_COPY = {
  heading: "What the artist’s estate can see",
  /**
   * The lead. It states the default out loud, because a switch already in the
   * permissive position is the one a reader is most likely to misread as off.
   */
  lead:
    "The artist’s estate reads this work’s history and its documents, and that " +
    "is not something you can turn off. It is also told that you own the work. " +
    "You can withhold your name, and you can separately show it where the work " +
    "is kept.",

  identityTitle: "Name me as the owner",
  identityShown: "The estate sees your name on this work.",
  /**
   * It says "we will not name you" and not "the estate will never see your
   * name", and the difference is the one `20261014091000` decision 13 argues:
   * this switch withholds ONDO's own statement of who owns the work. A line of
   * history that names you is a sentence you wrote, and the estate reads the
   * history.
   */
  identityHidden: "ONDO will not name you as the owner of this work.",

  accessTitle: "Show where it is kept",
  accessGranted: "The estate can see where this work is kept.",
  accessWithheld:
    "The estate sees the history, the documents and the photographs, and not " +
    "where the work is kept.",

  /**
   * Both switches, on a work with no estate behind them.
   *
   * It names the reason rather than greying out in silence: nobody has linked
   * the artist to an estate on ONDO, so there is no one for either control to
   * act on. It does not say who the estate would be, because that is not a
   * thing an owner is told.
   */
  noEstate: "No estate is registered for this artist, so neither setting does anything yet.",

  /** Shown while a switch is in flight, and on a refusal. */
  saving: "Saving…",
  failed: "That didn’t save. Your settings are unchanged.",
} as const;

/**
 * How far this work travels — OND-47, the owner's own control.
 *
 * THREE CHOICES AND THE OWNER WAS OFFERED FOUR. "Anyone on ONDO" is not here,
 * because there is nowhere to publish into yet; the panel does not mention a
 * fourth option at all, rather than showing one greyed out. A control a
 * collector can see and cannot use is a promise, and this ticket was told not
 * to make it.
 *
 * EVERY SENTENCE SAYS WHAT THE SETTING DOES TODAY, not what it sounds like.
 * "Private" in particular does not mean nobody can see the work: the people the
 * owner has already named still can, and the honest sentence says so. A screen
 * that implied otherwise would be the more dangerous of the two errors.
 */
export const WORK_VISIBILITY_COPY = {
  heading: "How far this work travels",
  lead:
    "You choose who this work reaches. Changing it applies straight away, and " +
    "never takes the work away from someone you have already shared it with.",

  privateTitle: "Private",
  privateLine:
    "Only you and the people and firms you have named. No new share links can " +
    "be made, and any link already out there stops working.",

  namedTitle: "Named parties only",
  namedLine:
    "The people and firms you have named, and anyone you give a share link to. " +
    "Nobody is told where the work is kept.",

  regionTitle: "Region only",
  regionLine:
    "As above, and anyone who can already see this work may also be told which " +
    "country it is in — never the address, the city, or anything else about " +
    "where it is kept.",

  /**
   * Shown under "Region only" when the work's location carries no country.
   *
   * The setting is real and does nothing on this work, and that is a fact
   * about the location rather than about the setting — nothing guessed a
   * country for a location recorded before the field existed. So the line
   * names the remedy instead of disabling the choice: an owner who means to
   * publish a region should be able to select it and then be told what is
   * missing.
   */
  regionMissing:
    "You haven’t recorded which country this work is in, so this setting " +
    "discloses nothing yet. Add a country to the location below.",

  /**
   * The fourth rung — OND-172, the setting OND-47 parked because there was
   * nowhere to publish into.
   *
   * THE LINE NAMES THE FIVE FIELDS AND NOTHING ELSE, because the five fields are
   * the whole of it and an owner pressing this is entitled to know exactly what
   * leaves. It says the history stays private in so many words: that is the one
   * thing a collector would most assume was included, and the ticket decided it
   * is not.
   */
  publicTitle: "Anyone on ONDO",
  publicLine:
    "Anybody with an ONDO account can see this work’s title, the artist, the " +
    "year, the medium and one photograph. Nothing else — not your name, not a " +
    "price, not the history or who has signed it, and not where the work is.",

  /** The separate opt-in, which publishing the work never turns on. */
  locationLabel: "Also show where this work is kept",

  /**
   * Shown while the switch is OFF, which is the only moment it can inform a
   * choice — OND-172 asks for "a plain warning at the moment it is switched on,
   * saying what it discloses".
   *
   * It is deliberately concrete. "Publishes your location" is a setting; "a
   * publicly readable address for a valuable object, attached to a named work"
   * is the thing that happens, and it is the re-identification risk OND-39
   * accepted on the owner’s behalf finally landing on a screen.
   */
  locationWarning:
    "Off. Turning this on puts the address you have recorded on a page anybody " +
    "with an ONDO account can open, next to the artist’s name and the year — " +
    "which is often enough for a reader to work out whose collection it is, even " +
    "though your name is not shown. Making a work public never turns this on.",

  /** On, and the work is public: the one state where it actually discloses. */
  locationLive:
    "On. The address, city and country you have recorded are on this work’s " +
    "public view.",

  /**
   * On, and the work is NOT public.
   *
   * The state an owner would otherwise read as broken: the choice stands and
   * shows nobody anything, because there is no public view to put it on.
   */
  locationDormant:
    "On, and showing nobody anything: this work is not public yet. Choose " +
    "“Anyone on ONDO” above and the location goes with it.",

  /** On, with no location recorded. `has_location` exists for this sentence. */
  locationMissing:
    "On, and showing nobody anything: you haven’t recorded where this work is. " +
    "Add a location below.",

  /**
   * The exception the owner cannot switch off — OND-66, and the 30 September
   * note asking for it to be visible here "rather than discovered later".
   *
   * It is on the panel at every setting, not only at Private, because a reader
   * choosing between the three is exactly the reader who needs to know that
   * one audience is outside the choice. It does not name the estate.
   */
  estateException:
    "One exception: if this artist has an estate on ONDO, it can see this work " +
    "whichever setting you choose.",

  saving: "Saving…",
  failed: "That didn’t save. This work’s setting is unchanged.",
} as const;

/**
 * The attributed estate’s half of a work’s public view — OND-172.
 *
 * A PANEL OF ITS OWN BECAUSE IT IS A DIFFERENT PARTY’S, not a different tab of
 * the owner’s. The ticket splits the public view in two along a line that does
 * not overlap: the artist, the year and whether the attribution is contested are
 * the estate’s; the title, the medium, the photograph, the location and whether
 * the work is shown at all are the owner’s. So the estate gets its own heading,
 * its own sentence about what it controls, and no sight of the owner’s rung —
 * only whether the work is published at all, which is public once it is true.
 */
export const WORK_ATTRIBUTION_VISIBILITY_COPY = {
  heading: "This artist’s name on the public view",
  lead:
    "You decide whether a public view of this work names the artist and the " +
    "year. Everything else on it — the title, the medium, the photograph and " +
    "whether the work is shown at all — is the owner’s to decide, and you " +
    "cannot change it.",

  label: "Show the artist and the year",

  /** On, and the owner has published: the name is out there. */
  publishedLive:
    "On. This work’s public view names the artist and the year.",
  /** On, and the owner has not published: nothing is showing. */
  publishedDormant:
    "On, and showing nobody anything: the owner has not made this work public.",
  /** Off: the public view carries a title, a medium and a photograph. */
  withheld:
    "Off. A public view of this work shows no artist and no year. The owner can " +
    "still publish the title, the medium and a photograph.",

  /**
   * The contested mark, which is DERIVED and not a control.
   *
   * Said here so the panel does not look like it is missing a switch: an estate
   * contests an attribution by disputing the claim on the work’s history, in its
   * own name, and the public view reports that. There is no toggle for it and
   * there is deliberately no column anywhere storing one.
   */
  contested:
    "This work’s attribution is disputed, and a public view says so — because " +
    "you disputed the claim on its history, not because of a setting here.",
  notContested:
    "Nobody with authority for this artist has disputed this work’s " +
    "attribution. To say a public view should mark it as contested, dispute the " +
    "authorship claim on the work’s history.",

  /** Off, and therefore no name to mark. The reason the line above disappears. */
  contestedWithheld:
    "While the artist’s name is hidden, a public view does not mark the " +
    "attribution as contested either — there is no name on it to mark.",

  saving: "Saving…",
  failed: "That didn’t save. This work’s setting is unchanged.",
} as const;

export const WORK_IMAGE_COPY = {
  loading: "Loading photographs…",
  /** The gallery's accessible name. Several tiles of one work need one label. */
  galleryLabel: "Photographs of this work",
  empty:
    "No photographs yet. Views uploaded when a work is registered appear here — front, verso, signature, and any detail worth recording.",
  /**
   * Storage refused the bytes, which is a state and not a fault: a caller
   * holding a work grant may read the row and still be outside the folder.
   */
  unreadable: "This photograph is not available to you.",
  failed:
    "The photographs could not be loaded. The rest of this record is unaffected — reload to try again.",
} as const;

/**
 * The tabs the record carries, in the order the design puts them.
 *
 * FOUR OF THESE ARE FILTERS OVER THE ONE HISTORY, not records of their own, and
 * that is the shape the eight kinds bought. Exhibitions, publications, documents
 * and condition were "planned" panels naming a table they were waiting for until
 * 20260820090100 gave `provenance_entries` a `kind`; the table they were waiting
 * for turned out to be the one that already existed. `kinds` is which entries the
 * tab shows, and a tab that filters is reading the same rows, with the same
 * attestations, under the same freeze.
 *
 * `state` says whether the registry can answer the question the tab asks, and it
 * is the same word `DESTINATIONS` in collector.constants.ts uses for the same
 * reason: a tab the registry could not answer would be reachable rather than
 * hidden, because a tab that names what is missing tells the reader more than
 * one that is not there.
 *
 * **THERE IS NO LONGER A PLANNED TAB ON THIS RECORD, AND THE ONE THAT WENT IS
 * OND-38's ANSWER.** Catalogue Raisonné sat here saying it needed "a catalogue
 * table of its own" and the artist as a principal — and the product manager
 * answered the ticket's either/or the other way: the publications tab IS the
 * answer, the word is "significant publication", and there is no structured
 * citation to wait for. A tab promising one would now be the registry
 * advertising a thing it has decided against, so it is removed rather than
 * relabelled; `20261014090100_a_significant_publication_is_the_citation.sql`
 * decision 6 is the argument, and a stale `?tab=catalogue` link lands on the
 * whole history because `parseTab` matches this list exactly.
 *
 * `state` keeps its third value nowhere: with nothing planned, the two states
 * are what a record can be read as. The machinery that drew an unanswered tab
 * went with it — git has it for the day a tab is waiting on something again,
 * and an unreachable branch that nothing can produce is how a screen rots.
 */
export const WORK_TABS = [
  { id: "provenance", label: "Artwork history", state: "ready", kinds: null },
  {
    id: "publications",
    label: "Significant publications",
    state: "ready",
    kinds: ["bibliography"],
  },
  { id: "exhibitions", label: "Exhibitions", state: "ready", kinds: ["exhibition", "loan"] },
  { id: "documents", label: "Documents", state: "ready", kinds: ["document"] },
  { id: "condition", label: "Condition", state: "ready", kinds: ["condition"] },
  // THE RECORD TAB — OND-53. Not a filter on the history and not a panel that
  // is waiting for something: a third state, so neither of the two loops in
  // `work.model.test.ts` can mistake it for either. It is last because it is
  // about the record rather than part of it.
  { id: "record", label: "Record", state: "witness", kinds: null },
] as const satisfies readonly {
  id: string;
  label: string;
  state: "ready" | "witness";
  kinds: readonly ProvenanceEntryKind[] | null;
}[];

export type WorkTabId = (typeof WORK_TABS)[number]["id"];

/** Where a record opens when the URL does not say. The whole history. */
export const DEFAULT_WORK_TAB: WorkTabId = "provenance";

/** A tab that narrows the history, as against showing all of it or none of it. */
export type FilteringTab = Extract<
  (typeof WORK_TABS)[number],
  { kinds: readonly ProvenanceEntryKind[] }
>;

/**
 * The tabs that need copy of their own.
 *
 * Derived from `kinds` rather than listed, so it stays one fact in one place:
 * Artwork history and Record are both excluded for free because neither narrows
 * anything, and a fifth filtering tab joins this type the moment it is added to
 * WORK_TABS — which is what makes the two tables below fail to compile until
 * somebody writes its sentences.
 */
export type FilteringTabId = FilteringTab["id"];

/**
 * What each filtering tab holds, in the kind's own terms.
 *
 * One sentence per tab and not one generic one, because "narrowed to one kind of
 * entry" told a reader nothing they could not already see. What they need is
 * which of the ten kinds this tab carries and which it does not — an exhibition
 * catalogue is a significant publication and not Exhibitions, a loan is on
 * Exhibitions because possession moved and title did not, and a scan attached to
 * a sale as evidence is not a Document. `toPanel` adds
 * WORK_RECORD_COPY.filteredNote, so the filter fact itself is said once.
 *
 * THE PUBLICATIONS SENTENCE ANSWERS A QUESTION THE RECORD USED TO HAVE A TAB
 * FOR. OND-38: a catalogue raisonné reference is recorded here, as a book and
 * where in it, and ONDO issues no entry number from it — the work's number in
 * this registry comes from a confirmed authorship claim and from nothing else
 * (`20261004096000`). Saying so here is the whole of "the publications tab is
 * the answer and should say so".
 */
export const WORK_TAB_HELP: Record<FilteringTabId, string> = {
  publications:
    "Books, catalogues and articles this work appears in — a catalogue raisonné, a monograph, an exhibition catalogue, a periodical — and where in each: a page, a plate, or several of them in one line. A catalogue raisonné reference belongs here; the work’s own number in this registry is not issued by a citation but by a confirmed authorship claim.",
  exhibitions:
    "Where the work has been shown, and the loans that put it there. An exhibition records the showing; a loan records possession moving while title stayed where it was.",
  documents:
    "Papers that are on the record in their own right — a certificate, an invoice, an export licence, a valuation — each with what it is and who issued it. A document attached as evidence to another entry is not here; it is on that entry.",
  condition:
    "Dated assessments of the object itself, and who made them. The findings are the entry: a condition report with no findings is a name and a date attached to nothing.",
};

/**
 * What a filtering tab says when the record holds none of its kind.
 *
 * Not "coming soon" and not blank. An empty Exhibitions tab is now a statement
 * — nobody has recorded one — which is a different thing from the tab not
 * existing, and it says which button records the first.
 *
 * Keyed on `FilteringTabId` and not `string`: a `Record<string, string>` read
 * with `?? null` accepted a new tab with no copy and rendered a blank line.
 */
export const WORK_TAB_EMPTY: Record<FilteringTabId, string> = {
  publications:
    "No publications recorded. Add one with “+ Add history”, under Significant publication.",
  exhibitions:
    "No exhibitions or loans recorded. Add one with “+ Add history”, under Exhibition or Loan.",
  documents:
    "No documents recorded on the work. Add one with “+ Add history”, under Document. Documents attached as evidence to another entry are on that entry.",
  condition:
    "No condition reports recorded. Add one with “+ Add history”, under Condition report.",
};

/**
 * WHAT A PLANNED TAB WOULD SAY — AND THERE IS NO LONGER ONE, SO THERE IS NO
 * LONGER A TABLE OF IT.
 *
 * `WORK_TAB_NOT_YET` held one entry, Catalogue Raisonné, naming the table and
 * the registry act it was waiting on. OND-38 answered that it is waiting on
 * neither: the citation is the significant publication above. An empty table
 * keyed on an empty union does not typecheck and should not — so it is gone
 * rather than left as a shape nothing can fill, and `WORK_ASIDE_NOT_YET` below
 * is still the mechanism for the panels that ARE waiting on something.
 *
 * The convention it followed survives with them: named rather than "coming
 * soon", because saying which table or decision a gap waits on is what turns
 * this screen into the map of what is left to build.
 */

/**
 * The panels beside the photographs that the prototype shows and the registry
 * cannot yet fill.
 *
 * Rendered in place rather than left out, so the record reads as the design
 * intends and every gap says what it is waiting for.
 */
export const WORK_ASIDE_NOT_YET = {
  confirmation: {
    title: "Record confirmation",
    lead: "Field-by-field confirmation is not built yet.",
    detail:
      "Attestations attach to a link in the chain of title, not to a field of the catalogue record — nobody can yet sign for the medium alone. The bar and the per-field marks in the design need attestations that name a field, and a rule for who may make them.",
  },
  // `fingerprint` WAS HERE, and is gone rather than retitled again. It said
  // "nothing renders it as a mark, and a decorative stand-in would look like
  // something the registry had measured" — and something renders it as a mark
  // now. Its objection was answered rather than dropped: the bars are a
  // reversible encoding of the code, so nothing on screen is measured that was
  // not. See `@ondo/shared`'s `domain/ondoCode.ts`.
  owner: {
    title: "Current owner",
    lead: "The owning principal is not disclosed yet.",
    detail:
      "The record can say the collection this work sits in and whether it is yours. It cannot yet name the owner: a collection shared with you exposes no owner grant, so the holder is undisclosable by policy rather than merely unknown.",
  },
} as const;

/**
 * The ONDO code panel.
 *
 * The one place a work's code is shown, and the only way it reaches an agent:
 * there is no lookup, and `ondo.request_representation()` refuses every code it
 * was not given. So the panel says who it is for, because a collector who did
 * not know what it was would have no reason to pass it on and no reason not to.
 */
export const WORK_CODE_COPY = {
  title: "ONDO code",
  lead: "The work’s identifier in the registry.",
  detail:
    "Give this to an agent you want to represent the work, along with the artist’s name or the year — they need both. Anything they do with it comes back to you as a request you grant or decline.",
  copy: "Copy",
  copied: "Copied",
  /**
   * What the picture above the code means.
   *
   * One sentence, and deliberately not a legend: the full reading of the
   * colours is on About ONDO, where somebody looking for it will go. A panel
   * that explained eight colours would be explaining seven the reader is not
   * looking at.
   */
  mark: "The mark above is this same code, drawn. Its colour is what the registry holds about the work today — About ONDO reads them.",
} as const;

/** The tag every not-yet panel wears, so a gap is never mistaken for an answer. */
export const NOT_YET_TAG = "Not built yet";

/**
 * Where each key moves the selection in the tab strip.
 *
 * A horizontal tablist, so Left and Right and not Up and Down. Anything absent
 * is a key the strip does not claim — the handler returns without preventing the
 * default, so typing still reaches the page.
 */
export const KEY_STEPS: Record<string, number | "home" | "end" | undefined> = {
  ArrowLeft: -1,
  ArrowRight: 1,
  Home: "home",
  End: "end",
};

/**
 * The agent panel and the modal that fills it.
 *
 * Copy for a feature whose refusals are written on the SERVER, which is the one
 * thing to know before editing this. `ondo.assign_work_agent()` raises the
 * sentence a collector sees when an id cannot be assigned, and
 * `POST /v1/eligibility` returns the same sentence on blur — so there is no
 * `notAnAgent` string here to drift out of step with the two places that say it.
 * What is here is everything the server has no opinion about.
 */
/**
 * Who represents this work, as a VIEWER reads it — OND-73.
 *
 * The owner manages their agents in the panel `WORK_AGENT_COPY` labels; this is
 * the other audience's panel, for a collector let in by a grant, a consignee, a
 * borrower, a co-agent: the buyer's question "is the person in front of me
 * authorised", answered from the record. Representation is kept visually
 * distinct from ownership — the eyebrow says REPRESENTED BY and never OWNER —
 * and an ended or expired appointment is drawn under its own heading so it can
 * never read as current.
 */
export const WORK_REPRESENTATION_COPY = {
  title: "Represented by",
  loading: "Loading representation…",
  current: "Current",
  past: "No longer",
  /** ONDO's word about the principal, not their own. See `principals.verified`. */
  verified: "Verified",
  mayConsign: "May consign",
  /** The firm the appointment reaches, when it does. */
  forFirm: (name: string) => `for ${name}`,
  since: (day: string) => `since ${day}`,
  until: (day: string) => `until ${day}`,
  ended: (day: string) => `ended ${day}`,
  expired: (day: string) => `expired ${day}`,
  /** Not "owner": the one thing this panel exists to keep distinct. */
  notTheOwner: "A representative acts for the owner and is not the owner.",
} as const;

export const WORK_AGENT_COPY = {
  title: "Represented by",
  /** Present only when `capabilities.assignAgent` is true. */
  assign: "Assign agent",
  /**
   * Not "No agents". A collector who has never assigned one should read this as
   * a state of the record rather than an empty list they failed to fill.
   */
  empty: "No agent has been assigned to this work.",
  loading: "Loading agents…",
  /** ONDO's word about the principal, not their own. See `principals.verified`. */
  verified: "Verified",
  end: "End",
  /**
   * The modal.
   *
   * The subtitle is the same sentence the "Add to artwork history" modal uses
   * about attaching an ONDO id, and it is load-bearing rather than decorative:
   * there is no name search in this product and there will not be one, so a
   * collector who expects to type "Eleanor" needs telling once, here, rather
   * than discovering it by typing.
   */
  dialogTitle: "Assign an agent",
  dialogSubtitle:
    "An ONDO id is attached, never discovered — there is no name search on ONDO.",
  idLabel: "Agent’s ONDO id",
  idPlaceholder: "ONDO-0000",
  noteLabel: "Why (optional)",
  notePlaceholder: "For the Basel consignment",

  /**
   * The term, and the two words that do the work are "optional" and "no".
   *
   * OPEN-ENDED IS THE DEFAULT AND THE FIELD SAYS SO, because the alternative is
   * a date picker that looks like it wants filling in. OND-76's answer was
   * explicit — "Open-ended by default, so a term is something you opt into" —
   * and an empty field with that hint is how an opt-in looks.
   *
   * NO SHORTCUTS: no "6 months", no "end of year". A term is an agreement
   * between two people, and a suggested one is the product having an opinion
   * about somebody else's arrangement — which is the same restraint
   * `consign.constants.ts` keeps about a floor.
   */
  termLabel: "Ends on (optional)",
  termHint: "Leave empty for an open-ended appointment.",

  /**
   * The delegation permission, in the owner's own words.
   *
   * ONE CHECKBOX AND NOT A DIAL, which is the recorded decision: "When the
   * collector assigns an agent they should be able to say they allow agent
   * delegation. If so, the agent can assign other agents. If not, the work is
   * locked to that agent only." The hint says what it COSTS rather than what it
   * is, because "allow delegation" tells a collector nothing about who ends up
   * holding their picture.
   *
   * THE HINT NAMES THE BOUND TOO. One level is the whole of it — a delegate
   * cannot pass it on, by check constraint — and that is the fact that makes the
   * box safe to tick, so it is the fact the box should state.
   */
  delegateLabel: "This agent may appoint another agent",
  delegateHint:
    "They can bring in one other agent, who cannot bring in anybody else. Ending this appointment ends theirs.",

  /**
   * The agreed terms, which are OPTIONAL and are NOT a consignment's.
   *
   * THE SECTION IS LABELLED "agreed with this agent" FOR ONE REASON. OND-43:
   * "distinguish an advisor's agreed terms from the effective floor on a gallery
   * consignment." A work may be on consignment to a gallery at one floor while an
   * advisor is appointed over it at another, and a form that said only "Floor"
   * would be the first place a dealer read the wrong number as binding.
   */
  termsHeading: "Agreed with this agent (optional)",
  arrangementLabel: "Arrangement",
  basisLabel: "Basis",
  commissionLabel: "Commission %",
  feeLabel: "Or a flat fee",
  askingLabel: "Agreed asking price",
  floorLabel: "Agreed floor",
  currencyLabel: "Currency",
  unset: "Not stated",

  /** The two refusals the form can make for itself. See `agentTermsProblem`. */
  floorOverAsking: "The agreed floor cannot be above the agreed asking price.",
  commissionAndFee:
    "Give a commission or a flat fee, not both — nothing would say how they combine.",

  /**
   * Renewal, which either side may do alone.
   *
   * "EXTEND" RATHER THAN "RENEW" ON THE BUTTON, because renew reads as putting
   * something back the way it was and this only ever moves the date forward: the
   * database refuses a date that does not extend the term. The confirmation says
   * who can do it, because OND-76's whole point is that neither party has to wait
   * for the other — "the other side's additional approval is not required".
   */
  extend: "Extend",
  extendTitle: "Extend this appointment",
  extendHint:
    "Either of you can extend it on your own. The other side is told, and does not need to approve it.",
  extendLabel: "New end date",
  extending: "Extending…",
  /** Said on the row itself, where the term is. See `mandateDaysRemaining`. */
  expired: "Term ended",
  endsToday: "Ends today",
  remaining: (days: number) => `${days} ${days === 1 ? "day" : "days"} left`,
  openEnded: "No end date",
  /** Who brought this agent in, when it was not the owner or the gallery. */
  delegatedBy: (ondoId: string) => `Appointed by ${ondoId}`,
  mayDelegateTag: "May delegate",

  /**
   * ── WHO THE APPOINTMENT REACHES, OND-83 ──
   *
   * TWO CHOICES AND THE SCREEN NAMES THE NARROWER ONE TOO. "This agent only" is
   * said out loud rather than left as the absence of a firm, because the whole
   * question a collector is answering is whether colleagues get in — and a row
   * that said nothing would leave them to assume whichever answer they already
   * believed. OND-83's example is a person reading it exactly that way: "If
   * Michael chooses 'Daniel only', David cannot use the appointment merely
   * because he is Daniel's colleague."
   *
   * "ALSO REACHES", NOT "AT". `at Marlow & Finch` would read as the agent's
   * employer, which this is NOT: the firm is named by whoever made the
   * appointment and is never read off the agent's memberships. An agent who
   * changes firms neither retargets this grant nor loses it, and a label
   * implying employment would make the next change of job look like a bug.
   */
  agentOnly: "This agent only",
  alsoReaches: (firm: string) => `Also reaches ${firm}`,
  /**
   * The hint under the organisation field, which names what it COSTS.
   *
   * `delegateHint`'s rule: "allow delegation" told a collector nothing about who
   * ends up holding their picture, so that hint says what the box does. The same
   * applies here, and the fact that makes including a firm safe is that the
   * firm's own seniority still decides — so that is the fact the field states.
   */
  organisationLabel: "Also include an organisation (optional)",
  organisationHint:
    "Their colleagues at that organisation can work on this piece, each at the level the organisation gives them. Leave it empty and the appointment reaches this agent alone. If they change firms later, nothing moves on its own.",

  /**
   * ── WHAT THE APPOINTMENT PERMITS, OND-82 ──
   *
   * "AGREE A CONSIGNMENT" AND NEVER "SELL". The ticket is explicit that the old
   * wording was the problem: "This action agrees a consignment: the gallery
   * takes the work to sell it. It is not itself a sale to a buyer or a transfer
   * of ownership." So the permission is named for what it permits, and the hint
   * says the two things a collector needs before ticking it — that the gallery
   * still answers, and that they will not be asked again each time.
   */
  consignLabel: "This agent may agree a consignment for me",
  // OND-79. The label says what the permission is; the note says what it is
  // NOT, because the obvious reading of "manage who sees what" is that the
  // owner is handing over their choices, and an owner who believed that would
  // never grant it. An agent's rules can only ever withhold more.
  discloseLabel: "This agent may narrow who sees what, on this work",
  discloseNote: "They can hide more than I have, never less — and I can undo it.",
  consignHint:
    "They can offer this work to a gallery on your behalf. The gallery accepts or declines, and you are told — but you will not be asked to approve each offer, because this is where you approved it. Untick it and they cannot make new offers.",
  mayConsignTag: "May agree consignments",

  /**
   * ── REVIEWING AND UPDATING A RECORDED SCOPE ──
   *
   * OND-83: "Let authorised parties review and update the recorded scope through
   * the appointment permission controls. Record changes."
   *
   * "CHANGE" RATHER THAN "EDIT", and the hint says the change is recorded —
   * because it is: `ondo.work_agent_mandate_scope_changes` keeps both sides of
   * it, which authority was used and which person used it. A collector who knows
   * a change leaves a trail reads the control differently from one who thinks it
   * quietly overwrites.
   *
   * THE CONTROL IS GATED ON `canSetScope`, WHICH IS NARROWER THAN `canEnd`. An
   * agent reading their own appointment sees End and does not see this, which is
   * decision 10 of 20261003099000: the appointed side does not widen its own
   * scope.
   */
  scopeHeading: "What this appointment reaches and permits",
  scopeSave: "Save scope",
  scopeSaving: "Saving…",
  scopeUnchanged: "That is the scope this appointment already has.",
  scopeHint:
    "Changes are recorded against the appointment, with who made them. The original appointment stays as it is.",

  checking: "Checking…",
  submit: "Assign",
  submitting: "Assigning…",
  cancel: "Cancel",
  /**
   * The ✕, and NOT `cancel`.
   *
   * The two do different things — ✕ puts the draft down, Cancel discards it —
   * so labelling ✕ "Cancel" would tell a screen-reader user the opposite of what
   * it does, and leave two controls with one name for anyone querying by it.
   */
  close: "Close",
  /** Shape only. Whether the id EXISTS is the server's answer, never this one. */
  malformed: "That is not an ONDO id.",
  /**
   * What ending one does, said before it is done rather than after.
   *
   * Specifically that the history they wrote survives, because that is the part
   * a collector would otherwise assume goes with them — an entry is frozen the
   * moment it is recorded, and revoking a mandate removes access and nothing
   * else.
   */
  endWarning:
    "They lose access to this record. Anything they added to its history stands.",
} as const;

/**
 * OND-79. Who sees which line of a work's history, and which fields of it.
 *
 * THE AUDIENCE LABELS ARE `Record<DisclosureAudience, string>` SO A FIFTH
 * AUDIENCE CANNOT ARRIVE WITHOUT COPY — the same device
 * `VISIBILITY_TITLES` uses, and the same reason: a missing label would render
 * as an empty heading on a disclosure control.
 *
 * `institution` SAYS WHAT IT COVERS, which is the honest wording of a schema
 * limit rather than a euphemism. The ticket names museums; `ondo.org_type` has
 * no museum and `ondo.capacity_for_org_type()` lands institutions,
 * conservators, experts and auction houses all on one capacity, so this is the
 * finest grain the server can derive — and an owner choosing who may see a sale
 * price has to know that before they choose, not after.
 */
export const DISCLOSURE_AUDIENCE_LABELS: Record<DisclosureAudience, string> = {
  collector: "Collectors",
  gallery: "Galleries and dealers",
  institution: "Museums, auction houses and other institutions",
  agent: "Agents and advisors",
};

/**
 * The field labels, keyed the same way and for the same reason.
 *
 * NAMED FOR WHAT AN OWNER WOULD WITHHOLD rather than for the columns behind
 * them. `insured_amount` is one label over two columns, because a sum without
 * its currency is a number nobody can act on; `documents` has no column at all
 * and is the attachments.
 */
export const PROVENANCE_FIELD_LABELS: Record<ProvenanceField, string> = {
  price: "What it changed hands for",
  counterparty: "Who the other party was",
  insurer: "Who insured it",
  policy_reference: "The policy number",
  insured_amount: "The sum insured",
  documents: "The attached documents",
};

export const WORK_DISCLOSURE_COPY = {
  heading: "Who sees which line",
  leadOwner:
    "Everyone you have given access to this work sees your default. You can " +
    "change what one kind of viewer sees, or restrict a single line of the history.",
  // The agent's own sentence, because the control means something narrower for
  // them and a panel that read the same would be promising more than it does.
  leadAgent:
    "You can narrow what this work's audiences see, within what its owner " +
    "already allows. You cannot reveal anything they have withheld.",
  defaultHeading: "Everyone with access",
  everyEntry: "The whole history",
  // An entry a rule names and the caller cannot read — which an agent's own
  // rule can do to them. The sentence says a line exists rather than pretending
  // the rule is about nothing.
  entryWithheld: "A line you cannot see",
  wholeEntryHidden: "The whole line is withheld",
  nothingWithheld: "Nothing withheld",
  // The empty OVERRIDE, which is not the same fact: at work level an override
  // replaces the default, so this one reveals everything the default hides.
  overrideRevealsAll: "Everything the default withholds is shown to them",
  inheritingLead: "On your default, with no rule of their own:",
  // A count and never a name: who represents the work is the agents panel's
  // disclosure, already made under the id-space rule.
  delegated: "An agent may narrow these rules.",
  delegatedMany: "Agents may narrow these rules.",
  addHeading: "Restrict something",
  audienceLabel: "Who this applies to",
  everyoneOption: "Everyone with access (the default)",
  entryLabel: "Which line",
  everyEntryOption: "The whole history",
  hideWholeLabel: "Withhold the whole line",
  fieldsLabel: "Withhold these",
  save: "Save rule",
  remove: "Remove",
  agentTag: "Set by an agent",
  saving: "Saving…",
  failed: "That rule was not saved. Nothing has changed.",
  empty: "No rules yet: everyone with access to this work sees all of it.",
} as const;

/**
 * The placement panel — OND-22. Where the work sits among the owner's own
 * collections, and the two acts that change it.
 *
 * The removal copy says what is actually lost, which is the owner's own read of
 * the record and nothing else: the work, its history and every attestation on it
 * are kept. A panel that said "delete" would be lying about the database.
 */
export const WORK_PLACEMENT_COPY = {
  heading: "Which collection holds it",
  lead:
    "Move this work between your own collections, or take it out of this one. " +
    "Nothing about the work or its history changes; only where you file it.",
  currently: "In",
  moveLabel: "Move to",
  moveAction: "Move",
  loadingTargets: "Finding your other collections…",
  noOtherCollections:
    "You have no other collection to move it to. Create one from your collection screen first.",
  removeAction: "Remove from this collection",
  removeArmedLead:
    "The work leaves this collection and stops appearing in your lists. Its record, " +
    "its history and every signature on it are kept, but you will no longer be able " +
    "to open it unless it is placed with you again.",
  removeConfirm: "Yes, remove it",
  removeCancel: "Keep it here",
  saving: "Saving…",
} as const;

/**
 * A correction is a second line — OND-41.
 *
 * "it will always show as corrected so its not just true … it wil show the
 * error for ever and that it was corrected." The mark says CORRECTED and what
 * the record first said; the panel says what a correction is and is not.
 */
export const WORK_CORRECTION_COPY = {
  mark: "Corrected",
  /** `{from}` is what the record said; `{by}` who corrected it; `{when}` the day. */
  markDetail: "The record first said “{from}”. Corrected by {by}, {when}.",
  markDetailEmpty: "The record first said nothing here. Corrected by {by}, {when}.",
  markTimes: "Corrected {times} times; the record first said “{from}”.",
  heading: "Correct a catalogue field",
  lead:
    "For a mistyped title, year, medium or dimensions. The correction is added to the " +
    "record, never written over it: what the record said stays visible for ever, " +
    "marked as corrected, beside what it says now. Who made the work is not a field " +
    "here — that is a claim an authority confirms or disputes.",
  fieldLabel: "Field",
  currently: "The record says",
  valueLabel: "Should say",
  action: "Add the correction",
  isFinal: "A correction cannot be edited or removed; a mistake in it is corrected the same way.",
} as const;
